import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { NavItem } from '@components/layout/src/layout.types';
import { SELECT_VENUE_ROUTE } from '@constants/routes';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { NavigationContextValue } from '@contexts/navigation/navigation.type';
import { useAppLaunchDetection } from '@hooks/use-app-launch-detection';
import { useMainSidebarMenu } from '@hooks/use-main-sidebar-menu';
import { getLastPathCookie, setLastPathCookie } from '@utils/last-path-storage/last-path-storage';

export const NavigationContext = createContext<NavigationContextValue>({
  navigationItems: [],
  isItemClicked: false,
  setNavigation: () => {},
  setIsItemClicked: () => {},
  preventRedirect: false,
  setPreventRedirect: () => {},
  redirectUrl: '',
  setRedirectUrl: () => {},
  route: {
    from: '',
    to: '',
  },
});

export const NavigationProvider = ({ children }: { children: ReactNode }) => {
  const [navigationItems, setNavigationItems] = useState<NavItem[]>([]);
  const [isItemClicked, setIsItemClicked] = useState<boolean>(false);
  const [preventRedirect, setPreventRedirect] = useState<boolean>(false);
  const [redirectUrl, setRedirectUrl] = useState<string>('');
  const location = useLocation();
  const sidebarMenu = useMainSidebarMenu();
  const { isWebView } = useContext(FeatureFlagContext);
  const { isInitialLoad, isRestoring, isInitialRedirect } = useAppLaunchDetection();

  // Store the full path including query parameters, but be smart about when to save
  const fullPath = `${location.pathname}${location.search}`;

  const [route, setRoute] = useState(() => ({
    to: fullPath,
    from: fullPath,
  }));

  // Save venue information when navigating
  useEffect(() => {
    // Only save path when it's a webview, not the initial load, not during restoration, not during initial redirect,
    // and not the select venue route (which is the default route)
    // Also prevent saving when query parameters are being added automatically by the page
    const currentStoredValue = getLastPathCookie();
    const isAddingQueryParams = location.search && !currentStoredValue?.includes(location.search);
    const shouldSavePath =
      isWebView &&
      !isInitialLoad &&
      !isRestoring &&
      !isInitialRedirect &&
      fullPath !== SELECT_VENUE_ROUTE &&
      !isAddingQueryParams;

    if (shouldSavePath) {
      setLastPathCookie(fullPath);
    }
  }, [isWebView, fullPath, isInitialLoad, isRestoring, isInitialRedirect, location.search]);

  useEffect(() => {
    if (fullPath !== route.to) {
      setRoute((prev) => ({ to: fullPath, from: prev.to }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullPath]);

  useEffect(() => {
    setNavigationItems(sidebarMenu ?? []);
  }, [sidebarMenu]);

  const contextValue = useMemo(
    () => ({
      navigationItems,
      isItemClicked,
      preventRedirect,
      redirectUrl,
      route,
      setNavigation: setNavigationItems,
      setIsItemClicked,
      setPreventRedirect,
      setRedirectUrl,
    }),
    [navigationItems, isItemClicked, preventRedirect, redirectUrl, route]
  );

  return <NavigationContext.Provider value={contextValue}>{children}</NavigationContext.Provider>;
};
